import { IElement } from "core-integration/src/store/elements/types";
import { IEvent } from "core-integration/src/store/events/types";
import { IPick } from "core-integration/src/store/my-team/types";
import ElementDialogButton from "plos/src/components/ElementDialogButton";
import ElementInTable from "plos/src/components/ElementInTable";
import { paginate, Paginator } from "plos/src/components/Paginator";
import { Cell, Column, Table } from "plos/src/components/Table";
import { rowStyles } from "plos/src/components/Table/tables.css";
import { getDifficultyColor } from "plos/src/styles/difficultyColors";
import { fixedTable, scrollContainer } from "plos/src/styles/utils.css";
import { integerToMoneyWithCurrency } from "plos/src/utils/money";
import { Row, TableBody, TableHeader } from "react-aria-components";
import { useTranslation } from "react-i18next";
import OwnershipCell from "../OwnershipCell/OwnershipCell";
import { PriceChangeDeadline } from "../PriceChangeDeadline";
import styles from "../priceChanges.module.css";
import PriceChangeStatus from "../PriceChangeStatus/PriceChangeStatus";
import { SortableColumn } from "../SortableColumn";
import { PriceChangesTableProps } from "../types";
import { getPriceChangeStatus, priceChangePredictionEnabled } from "../utils";

interface PredictionsTableProps extends PriceChangesTableProps {
  hasEntry: boolean;
  priceChangeElements: IElement[];
  currentEvent: IEvent | null;
  savedPicksByElement: Record<number, IPick>;
}

const PredictionsTable = ({
  hasEntry,
  priceChangeElements,
  currentEvent,
  showElementDialog,
  teamsById,
  currencyDivisor,
  handleSortChange,
  savedPicksByElement,
  sortDescriptor,
  page,
  setPage,
}: PredictionsTableProps) => {
  const { t } = useTranslation("priceChanges");

  // TODO: Remove the current event check when we are ready to show predicted progress from season start
  const predictionEnabled =
    priceChangePredictionEnabled && currentEvent && currentEvent.id >= 2;

  const { data, totalPages } = paginate(priceChangeElements, page, 20);

  return (
    <>
      <PriceChangeDeadline />
      <div className={scrollContainer}>
        <div className={styles.statsTableWrapper}>
          <Table
            className={fixedTable}
            aria-label={t("title", "Price Changes")!}
            sortDescriptor={sortDescriptor}
            onSortChange={handleSortChange}
          >
            <TableHeader>
              <Column className={styles.statsInfoCol} />
              <SortableColumn
                id="player"
                isRowHeader
                label={t("player", "Player")}
                className={styles.statsElementCol}
              />
              <SortableColumn
                id="status"
                label={t("status.columnHeader", "Status")}
                className={styles.statusCol}
              />
              <SortableColumn
                id="progress"
                label={t("progress", "Progress")}
                className={styles.progressCol}
              />
              {predictionEnabled && (
                <SortableColumn
                  id="predictedProgress"
                  label={t("predictedProgress", "Predicted Progress")}
                  className={styles.statColWide}
                />
              )}
              <SortableColumn
                id="ownership"
                label={t("ownershipTrend", "Ownership Trend")}
                className={styles.statColWide}
              />
              <SortableColumn
                id="currentPrice"
                label={t("currentPrice", "Current Price")}
                className={styles.priceCol}
              />
              {hasEntry && (
                <>
                  <SortableColumn
                    id="purchasePrice"
                    label={t("purchasePrice", "Purchase Price")}
                    className={styles.priceCol}
                  />
                  <SortableColumn
                    id="sellingPrice"
                    label={t("sellingPrice", "Selling Price")}
                    className={styles.priceCol}
                  />
                </>
              )}
            </TableHeader>
            <TableBody
              renderEmptyState={() => (
                <div className={styles.emptyState}>
                  {t("noChanges", "No price change predictions to display")}
                </div>
              )}
            >
              {data.map((e) => {
                const predictedProgress =
                  e.price_change_projections?.[0]?.projected_percent;

                const formattedPredictedProgress = `${
                  Number(predictedProgress) > 0 ? "+" : ""
                }${predictedProgress}%`;

                return (
                  <Row key={e.id} className={rowStyles}>
                    {/* Info */}
                    <Cell className={styles.statsInfoCell}>
                      <ElementDialogButton elementId={e.id} variant="list" />
                    </Cell>
                    {/* Player */}
                    <Cell className={styles.statsElementCell}>
                      <ElementInTable
                        element={e}
                        team={teamsById[e.team]}
                        actionMe={() => showElementDialog(e.id)}
                        renderDreamTeam={e.in_dreamteam}
                      />
                    </Cell>
                    {/* Status */}
                    <Cell className={styles.statusCell}>
                      <PriceChangeStatus element={e} />
                    </Cell>
                    {/* Progress */}
                    <Cell>
                      <span
                        className={`${styles.chip} ${getDifficultyColor(
                          getPriceChangeStatus(e),
                        )}`}
                      >
                        {`${Number(e.price_change_percent) > 0 ? "+" : ""}${
                          e.price_change_percent
                        }%`}
                      </span>
                    </Cell>
                    {/* Predicted Progress */}
                    {predictionEnabled && (
                      <Cell>
                        <span
                          className={`${styles.chip} ${getDifficultyColor(
                            getPriceChangeStatus(e),
                          )}`}
                        >
                          {formattedPredictedProgress}
                        </span>
                      </Cell>
                    )}
                    {/* Ownership trend */}
                    <Cell>
                      <OwnershipCell element={e} />
                    </Cell>
                    {/* Current Price */}
                    <Cell>
                      {integerToMoneyWithCurrency(e.now_cost, currencyDivisor)}
                    </Cell>
                    {hasEntry && (
                      <>
                        {/* Purchase Price */}
                        <Cell>
                          {savedPicksByElement[e.id]
                            ? integerToMoneyWithCurrency(
                                savedPicksByElement[e.id].purchase_price,
                                currencyDivisor,
                              )
                            : "-"}
                        </Cell>
                        {/* Selling Price */}
                        <Cell>
                          {savedPicksByElement[e.id]
                            ? integerToMoneyWithCurrency(
                                savedPicksByElement[e.id].selling_price,
                                currencyDivisor,
                              )
                            : "-"}
                        </Cell>
                      </>
                    )}
                  </Row>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
      {totalPages > 0 && (
        <Paginator
          totalPages={totalPages}
          page={page}
          setPage={setPage}
          variant="text"
        />
      )}
    </>
  );
};

export default PredictionsTable;
